/**
 * Typed GA4 event helpers
 *
 * All conversion events and interaction events go through these helpers.
 * No raw gtag('event', ...) should appear elsewhere in the codebase.
 */

import { trackEvent } from './gtag';

// ─── Authentication & Onboarding ──────────────────────────────────────────

export function trackSignUpStarted() {
  trackEvent('sign_up_started');
}

export function trackSignUpCompleted() {
  trackEvent('sign_up_completed');
}

export function trackLogin() {
  trackEvent('login');
}

export function trackOnboardingStarted() {
  trackEvent('onboarding_started');
}

export function trackOnboardingCompleted() {
  trackEvent('onboarding_completed');
}

// ─── Agent Lifecycle ──────────────────────────────────────────────────────

export function trackAgentCreateStarted() {
  trackEvent('agent_create_started');
}

export function trackAgentCreateCompleted(agentName: string) {
  trackEvent('agent_create_completed', { agent_name: agentName });
}

export function trackFirstAgentCreated(agentName: string) {
  trackEvent('first_agent_created', { agent_name: agentName });
}

export function trackAgentDeleted() {
  trackEvent('agent_deleted');
}

export function trackAgentConfigured(action: string) {
  trackEvent('agent_configured', { action });
}

// ─── Subscription & Billing ───────────────────────────────────────────────

export function trackPricingPageViewed() {
  trackEvent('pricing_page_viewed');
}

export function trackPlanSelected(planName: string) {
  trackEvent('plan_selected', { plan_name: planName });
}

export function trackCheckoutStarted(planName: string, interval: string) {
  trackEvent('checkout_started', { plan_name: planName, billing_interval: interval });
}

export function trackSubscriptionCreated(planName: string, interval: string) {
  trackEvent('subscription_created', { plan_name: planName, billing_interval: interval });
}

export function trackSubscriptionUpgraded(planName: string) {
  trackEvent('subscription_upgraded', { plan_name: planName });
}

export function trackSubscriptionDowngraded(planName: string) {
  trackEvent('subscription_downgraded', { plan_name: planName });
}

export function trackSubscriptionCancelled() {
  trackEvent('subscription_cancelled');
}

export function trackCreditsPurchased(amount: number) {
  trackEvent('credits_purchased', { amount });
}

// ─── Global Interaction Events ────────────────────────────────────────────

export function trackButtonClick(label: string, page: string, component?: string) {
  trackEvent('button_click', {
    button_label: label,
    page,
    ...(component && { component }),
  });
}

export function trackCTAClick(label: string, page: string, destination?: string) {
  trackEvent('cta_click', {
    cta_label: label,
    page,
    ...(destination && { destination }),
  });
}

export function trackLinkClick(text: string, destination: string, isExternal: boolean) {
  trackEvent('link_click', {
    link_text: text,
    destination,
    is_external: isExternal,
  });
}

export function trackNavClick(item: string) {
  trackEvent('nav_click', { nav_item: item });
}

export function trackFormSubmit(formName: string, page: string) {
  trackEvent('form_submit', { form_name: formName, page });
}

/** Track input field focus. Never include sensitive field values. */
export function trackFieldFocus(fieldName: string, formName?: string) {
  trackEvent('field_focus', {
    field_name: fieldName,
    ...(formName && { form_name: formName }),
  });
}

/**
 * Track input field blur (abandonment signal).
 * Only records whether a value was entered, never the value itself.
 */
export function trackFieldBlur(fieldName: string, hasValue: boolean, formName?: string) {
  trackEvent('field_blur', {
    field_name: fieldName,
    has_value: hasValue,
    ...(formName && { form_name: formName }),
  });
}

export function trackSelectChange(fieldName: string, selectedValue: string) {
  trackEvent('select_change', { field_name: fieldName, selected_value: selectedValue });
}

export function trackToggleChange(fieldName: string, newState: boolean) {
  trackEvent('toggle_change', { field_name: fieldName, new_state: newState });
}
